import React from "react";
import { cn } from "../lib/utils";
import { Magnetic } from "./Magnetic";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
  isMagnetic?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  className,
  variant = "primary",
  size = "md",
  children,
  isMagnetic = true,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center whitespace-nowrap font-semibold transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none tracking-tight";

  // Salmon fill + CHARCOAL bold label, no hover-darken; secondary/outline/ghost
  // are ink on a hairline border.
  //
  // The label was white, which matched the RN app but measured 2.0:1 against
  // #FFA07A, under the 3.0 an 18px bold label needs. --color-on-salmon is
  // #2d3849 and reads 5.84:1 on the same fill. It also makes the site
  // self-consistent: eleven salmon CTAs already used the ink token and six,
  // including this one, still used white.
  //
  // NOTE this now DIVERGES from the native app, which is still white on salmon.
  const variants = {
    primary: "bg-salmon text-on-salmon font-bold hover:bg-salmon",
    outline: "border border-hairline text-ink hover:bg-ink/5 bg-transparent",
    ghost: "text-ink hover:bg-paper-tan",
  };

  const sizes = {
    sm: "h-9 px-4 text-sm rounded-full",
    md: "h-11 px-6 text-base rounded-full",
    lg: "h-12 px-8 text-lg rounded-full",
  };

  const buttonElement = (
    <button
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      {...props}
    >
      {children}
    </button>
  );

  if (isMagnetic) {
    return <Magnetic>{buttonElement}</Magnetic>;
  }

  return buttonElement;
};

export default Button;
